import { makeStyles } from '@material-ui/core';
import { useContext, useEffect, useState } from 'react';
import { prettyDate } from '../../../config/dateFormatting';
import { FrontendApi } from '../../../planEatApi/FrontendApi';
import { ISeatingBookedMenus } from '../../../interface/interface';
import { IBookingContent, IDishContent, ISeatingContent, IVersionedObject } from '../../../planEatApi/PlanEatFacade';
import clsx from 'clsx';
import { OfficeContext } from '../../../config/Context';

const useStyles = makeStyles((theme: any) => ({
    hrBottom: {
        borderBottom: '1px dashed rgba(11, 37, 89, 0.2);'
    },
    timesOptionRowContainer: {
        display: 'grid',
        gridTemplateColumns: '20% auto 200px',
        width: '100%',
        height: "min-content"
    },
    timesOptionRowContent: {
        display: 'flex',
        justifyContent: 'center',
        "& > *": {
            marginLeft: 30
        }
    },
    timesOptionTotalEst: {
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingRight: 60,
    },
    timesOptionRowContentTotal: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 0,
    },
    timesOptionRowTitleContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        float: 'left',
    },
    timesOptionHeader: {
        fontSize: 16,
        fontWeight: 700
    },
    timesOptionHeaderTotal: {
        fontSize: 10,
        marginTop: 20,
        paddingLeft: 2,
        paddingRight: 10,
        fontWeight: 700,
    },
    timesOptionHeaderEst: {
        fontSize: 10,
        marginTop: 20,
        paddingLeft: 2,
        fontWeight: 700,
    },
    totalHeader: {
        fontSize: 16,
        fontWeight: 700,
        marginTop: 20,
        paddingBottom: 20
    },
    timesOptionMenuDescription: {
        fontSize: 8,
        fontWeight: 400
    },
    timesOptionMenuItemContainer: {
        display: 'grid',
        gridTemplateColumns: '100%',
        justifyItems: 'center',
        float: 'left',
        width: 120
    },
    timesOptionEstimateContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems:'center',
        float: 'left',
        height: "100%",
        width: 120,
    },
    timesOptionSeatingTime: {
        fontSize: 12,
        fontWeight: 700
    },
    timesOptionSeatingName: {
        paddingLeft: 5,
        fontSize: 12,
        fontWeight: 400
    },
    timesOptionBookedEstimatContainer: {
        fontSize: 12,
        fontWeight: 700,
    },
    timesOptionBookedEstFlex: {
        display: 'flex',
        float: 'left',
    },
    totalBookingsHeader: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingRight: 45,
        fontSize: 16,
        fontWeight: 700
    },
    timesOptionBookedEstFlexDish: {
        display: 'flex',
        float: 'right',
    },
    timesOptionCalcTotal: {
        fontSize: 30,
        fontWeight: 700
    },
    timesOptionEstimateContainerTotal: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: "center",
        float: 'left',
        width: 200,
        padding: 6,
        borderLeft: '1px dashed rgba(11, 37, 89, 0.2);',
        "& > *": {
            fontSize: 18,
            fontWeight: 700
        }
    },
    timesOptionBookedEstFlexTotal: {
        display: 'flex',
        float: 'left',
        paddingLeft: 5,
    },
    noSeatingsStyle: {
        fontSize: 18,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        background: '#FFFFFF',
        color: '#AAAAAA',
        marginTop: 18,
        borderRadius: '8px 8px 0px 0px',
        paddingBottom: 60
    },
    estimateColor: {
        color: theme.palette.primary.main,
        opacity: 0.5
    }
}));

interface Props {
    date: Date,
    seatings:IVersionedObject<ISeatingContent>[]; 
    bookings:IVersionedObject<IBookingContent>[];
    dishes: IVersionedObject<IDishContent>[];
}

export const UpcomingBookings: React.FC<Props> = ({ date, seatings, bookings, dishes}) => {
    // Styling
    const classes = useStyles();

    // State
    const [dishesOnDate, setDishesOnDate] = useState<IVersionedObject<IDishContent>[]>([]);
    const [seatingBookedMenus, setSeatingBookedMenus] = useState<ISeatingBookedMenus[]>([]);
    const [timeOptionView, setTimeOptionView] = useState<any[]>([]);

    // Context
    const officeContext = useContext(OfficeContext);

    useEffect(() => {
        if(officeContext?.office){
            init();
        }
    }, [officeContext, date, bookings]);

    async function init(){
        if(officeContext?.office){
            getDisplayTimeOption(officeContext?.office.content.id);
        }
    }
    const calculateTotal = () => {
        var totalBooked = 0;
        var totalEstimate = 0;

        seatingBookedMenus.forEach((seatingBookedMenu) => {
            seatingBookedMenu.bookedDishes.forEach((bookedDish) => {
                totalBooked += bookedDish.booked;
                totalEstimate += bookedDish.estimated;
            })
        })
        return [totalBooked, totalEstimate]
    }

    const calculateDishTotal = (bookedMenu: ISeatingBookedMenus) => {
        var totalBooked = 0;
        var totalEstimate = 0;


        bookedMenu.bookedDishes.forEach((bookedDish) => {
            totalBooked += bookedDish.booked;
            totalEstimate += bookedDish.estimated;
        })

        return [totalBooked, totalEstimate];
    }

    const calculateSeatingTotal = (dishOnDate: IVersionedObject<IDishContent>) => {
        var totalBooked = 0;
        var totalEstimate = 0;

        seatingBookedMenus.forEach((seatingBookedMenu) => {
            seatingBookedMenu.bookedDishes.forEach((bookedDish) => {
                if (bookedDish.dishId === dishOnDate.content.id) {
                    totalBooked += bookedDish.booked;
                    totalEstimate += bookedDish.estimated;
                }
            })
        })
        return [totalBooked, totalEstimate]
    }

    const getDisplayTimeOption = async (officeId: string) => {
        // Get unique dishes on date
        FrontendApi.getDishesOnDate(date,seatings,dishes,bookings)
        .then((tmpDishesOnDate) => {
            setDishesOnDate(tmpDishesOnDate)
            FrontendApi.getSeatingsOnDate(date,officeId,seatings)
            .then((tmpSeatingsOnDate) => {
                FrontendApi.getSeatingBookedMenus(tmpDishesOnDate,tmpSeatingsOnDate,bookings)
                .then((seatingsBookedMenus) => {
                    setSeatingBookedMenus(seatingsBookedMenus)
                })
            })
        })
    }

    useEffect(() => {
        displayTimeOption();
    }, [seatingBookedMenus, dishesOnDate]);


    const displayTimeOption = () => {
        if (seatingBookedMenus.length > 0) {
            let tmpDisplayOptionTime: any[] = []

            const tmpOptionTimeHeader: any = (
                <div className={classes.timesOptionRowContainer}>
                    <div className={classes.timesOptionHeader}>
                        {prettyDate(date, false, true)}
                    </div>
                    <div className={classes.timesOptionRowContent}>
                        {dishesOnDate.map((dishOnDate, index) => (
                            <div className={classes.timesOptionMenuItemContainer}>
                                <div className={classes.timesOptionHeader}>
                                    Menu {index + 1}
                                </div>
                                <div className={classes.timesOptionMenuDescription}>
                                    {dishOnDate.content.dishDescription}
                                </div>

                            </div>
                        ))}
                    </div>
                    <div className={classes.totalBookingsHeader}>
                        Total bookings
                    </div>
                </div>
            );
            tmpDisplayOptionTime.push(tmpOptionTimeHeader)


            const tmpTimesOptionContent = seatingBookedMenus.map((seatingBookedMenuItem, index) => (
                <div className={clsx(classes.timesOptionRowContainer, classes.hrBottom)}>
                    <div className={classes.timesOptionRowTitleContainer}>
                        <div className={classes.timesOptionSeatingTime}>
                            {prettyDate(seatingBookedMenuItem.seatingTime, true, false)}
                        </div>
                        <div className={classes.timesOptionSeatingName}>
                            {seatingBookedMenuItem.seatingName}
                        </div>
                    </div>
                    <div className={classes.timesOptionRowContent}>
                        {dishesOnDate.map((dishOnDate, index2) => (
                            <div className={classes.timesOptionBookedEstimatContainer}>
                                {seatingBookedMenuItem.bookedDishes[index2]

                                    ?

                                    <div className={classes.timesOptionEstimateContainer}>
                                        <div className={classes.timesOptionBookedEstFlex}>
                                            {seatingBookedMenuItem.bookedDishes[index2].booked}

                                        </div>
                                        <div className={clsx(classes.timesOptionBookedEstFlex,classes.estimateColor)}>
                                            &nbsp; / {seatingBookedMenuItem.bookedDishes[index2].estimated}

                                        </div>
                                    </div>

                                    :

                                    <div className={classes.timesOptionEstimateContainer}>
                                    </div>
                                }
                            </div>
                        ))}


                    </div>
                    <div className={classes.timesOptionTotalEst}>
                        <div className={classes.timesOptionEstimateContainerTotal}>
                            <div className={classes.timesOptionBookedEstFlexDish}> 
                                {calculateDishTotal(seatingBookedMenuItem)[0]} 
                            </div>
                            <div className={clsx(classes.timesOptionBookedEstFlexDish,classes.estimateColor)}>
                                &nbsp; / {calculateDishTotal(seatingBookedMenuItem)[1]}
                            </div>
                        </div>
                    </div>
                </div>
            ));
            tmpDisplayOptionTime.push(tmpTimesOptionContent)
            let tmpTotalDisplay: any[] = [];
            tmpTotalDisplay.push(   
                <div className={classes.timesOptionRowContainer} style={{marginBottom:50}}>
                    <div className={classes.totalHeader}>
                        Total
                    </div>
                    <div className={classes.timesOptionRowContent}>
                        {dishesOnDate.map((dishOnDate, index) => (
                            <div className={classes.timesOptionEstimateContainer}>
                                <div className={classes.timesOptionBookedEstFlex} style={{fontSize:18,fontWeight:700}}>
                                    {calculateSeatingTotal(dishOnDate)[0]}
                                </div>
                                <div className={clsx(classes.timesOptionBookedEstFlex,classes.estimateColor)}style={{fontSize:18,fontWeight:700}}>
                                    &nbsp;  / {calculateSeatingTotal(dishOnDate)[1]}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={classes.timesOptionRowContentTotal}>
                        <div className={classes.timesOptionEstimateContainerTotal}>
                            <div className={classes.timesOptionBookedEstFlexTotal}>
                                <div className={classes.timesOptionCalcTotal}>
                                    {calculateTotal()[0]}
                                </div>
                                <div className={classes.timesOptionHeaderTotal}>
                                    total
                                </div>
                            </div>
                            <div className={clsx(classes.timesOptionBookedEstFlexTotal,classes.estimateColor)}>
                                <div className={classes.timesOptionCalcTotal}>
                                    {calculateTotal()[1]}
                                </div>
                                <div className={classes.timesOptionHeaderEst}>
                                    estimate
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
            tmpDisplayOptionTime.push(tmpTotalDisplay);
            setTimeOptionView(tmpDisplayOptionTime)
        } else {
            const tmpNoSeatingsOnDate: any = (
                <div>
                    <div className={classes.timesOptionHeader}>
                        {prettyDate(date, false, true)}
                    </div>
                    <div className={classes.noSeatingsStyle}>
                        No seatings on the date
                    </div>
                </div>
            );
            setTimeOptionView(tmpNoSeatingsOnDate)
        }
    }

    return (
        <div>
            {timeOptionView}
        </div>
    );
}